import React, { useMemo, useState } from 'react';
import { Plus, MapPin, Trash2, Pencil, X } from 'lucide-react';
import { loadDevJson, saveDevJson } from '../../lib/devStorage';
import { PageSearchBar } from '../common/PageSearchBar';
import { SERVICE_LINES_KEY, writeCatalog } from '../../lib/financeCatalogs';
import { useTranslation } from '../../lib/i18n';

const LINES_KEY = SERVICE_LINES_KEY;

type LineRow = { id: string; name: string };

function readLines(): LineRow[] {
  const raw = loadDevJson<unknown>(LINES_KEY, []);
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if (typeof raw[0] === 'string') {
    const migrated = (raw as string[])
      .map((name) => ({ id: crypto.randomUUID(), name: String(name).trim() }))
      .filter((r) => r.name);
    saveDevJson(LINES_KEY, migrated);
    return migrated;
  }
  return (raw as LineRow[]).filter((r) => r && typeof (r as LineRow).name === 'string');
}

function writeLines(rows: LineRow[]) {
  writeCatalog(LINES_KEY, rows);
}

const LineSettings: React.FC = () => {
  const { t } = useTranslation();
  const [lines, setLines] = useState<LineRow[]>(() => readLines());
  const [search, setSearch] = useState('');
  const [newLine, setNewLine] = useState('');
  const [editing, setEditing] = useState<LineRow | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lines;
    return lines.filter((l) => l.name.toLowerCase().includes(q));
  }, [lines, search]);

  const persist = (next: LineRow[]) => {
    setLines(next);
    writeLines(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newLine.trim()) {
      persist([{ id: crypto.randomUUID(), name: newLine.trim() }, ...lines]);
      setNewLine('');
    }
  };

  const removeLine = (id: string) => {
    persist(lines.filter((l) => l.id !== id));
  };

  const saveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing || !editing.name.trim()) return;
    persist(lines.map((l) => (l.id === editing.id ? { ...l, name: editing.name.trim() } : l)));
    setEditing(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-zinc-900">
        <MapPin className="h-5 w-5 text-emerald-700" aria-hidden />
        <p className="text-sm text-zinc-600">{t('settings.lineDesc')}</p>
      </div>

      <PageSearchBar value={search} onChange={setSearch} placeholder={t('common.search')} className="max-w-full" />

      <form onSubmit={handleSubmit} className="rounded-md border border-zinc-200 bg-zinc-50/50 p-4">
        <label className="app-label">{t('settingsForm.addLine')}</label>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={newLine}
            onChange={(e) => setNewLine(e.target.value)}
            placeholder="Village, town, or area name"
            className="app-field flex-1"
          />
          <button type="submit" className="app-btn-primary inline-flex shrink-0 items-center gap-2">
            <Plus className="h-4 w-4" />
            {t('common.add')}
          </button>
        </div>
      </form>

      <div className="overflow-hidden rounded-md border border-zinc-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50/80 px-3 py-2">
          <h3 className="text-sm font-semibold text-zinc-900">{t('settingsForm.serviceLines')}</h3>
          <span className="rounded-full border border-zinc-200 bg-white px-2.5 py-0.5 text-xs font-medium text-zinc-600">
            {filtered.length} of {lines.length}
          </span>
        </div>

        {lines.length === 0 ? (
          <p className="border-t border-dashed border-zinc-200 bg-zinc-50/60 py-8 text-center text-sm text-zinc-500">
            {t('settings.lineDesc')}
          </p>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-500">{t('common.noMatch')}</p>
        ) : (
          <div className="max-h-[360px] overflow-y-auto">
            <table className="app-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="w-14 text-center">#</th>
                  <th>{t('settingsForm.serviceLines')}</th>
                  <th className="text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((line, index) => (
                  <tr key={line.id} className="hover:bg-zinc-50/70">
                    <td className="text-center font-mono text-zinc-500">{index + 1}</td>
                    <td className="font-medium text-zinc-800">{line.name}</td>
                    <td>
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing({ ...line })}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-zinc-700 hover:bg-zinc-100"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          {t('common.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLine(line.id)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4 backdrop-blur-sm">
          <div className="app-card w-full max-w-md shadow-lg">
            <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-zinc-900">{t('common.edit')} {t('settings.line')}</h3>
              <button type="button" onClick={() => setEditing(null)} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={saveEdit} className="app-card-inner space-y-4">
              <div>
                <label className="app-label">{t('common.name')}</label>
                <input className="app-field" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditing(null)} className="app-btn-secondary">
                  {t('common.cancel')}
                </button>
                <button type="submit" className="app-btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineSettings;
